import { useMemo } from 'react'

import type { FullShipment, ListShipment, Quote } from '@fv/client-types'
import type { Currency } from '@fv/models'
import type { EquipmentType } from '@fv/models/equipment'

import { useActiveResponses } from './useActiveResponses'
import { useInactiveResponses } from './useInactiveResponses'

export type QuoteRequestResponse = {
  amount?: number
  amountUsd?: number
  carrierId?: string
  currency?: Currency
  email?: string
  equipmentType?: EquipmentType
  errors?: Quote['errors']
  hasDeclined?: boolean
  hasResponded?: boolean
  id: string
  isProcessing?: boolean
  lastResponseDate?: string
  method?: Quote['method']
  name: string
  source?: Quote['source']
  status?: Quote['status']
  userId?: string
  pricingMethod?: Quote['pricingMethod']
  pricingType?: Quote['pricingType']
  charges?: Quote['charges']
  serviceId?: Quote['serviceId']
  providerName?: string
  isSpot?: boolean
  quoteNum?: string | null
  deliveryDate?: Quote['deliveryDate']
}

export type QuoteRequestResponses = {
  active: QuoteRequestResponse[]
  declined: QuoteRequestResponse[]
  pending: QuoteRequestResponse[]
}

export function useQuoteRequestResponses(opts: {
  filter?: 'date' | 'price'
  includePendingApiRates?: boolean
  load?: FullShipment | ListShipment
}): QuoteRequestResponses {
  const activeResponses = useActiveResponses(opts)
  const inactiveResponses = useInactiveResponses(opts.load?.loadId)

  return useMemo(
    () => ({
      active: activeResponses,
      declined: inactiveResponses.filter(r => r.hasDeclined),
      pending: inactiveResponses.filter(r => !r.hasResponded && !r.hasDeclined),
    }),
    [activeResponses, inactiveResponses],
  )
}
